import React, { useState } from 'react';
import { Box, Button, Flex, Grid, Text } from '@mantine/core';
import { Image } from '../../Image';
import RecommendationCollarDesktopImage from '../../../assets/images/recommendation_collar_desktop.png';
import RecommendationCollarMobileImage from '../../../assets/images/recommendation_collar_mobile.png';
import RecommendationMiniDogDesktopImage from '../../../assets/images/recommendation_mini_dog_desktop.png';
import RecommendationMiniDogMobileImage from '../../../assets/images/recommendation_mini_dog_mobile.png';
import RecommendationMiniCatDesktopImage from '../../../assets/images/recommendation_mini_cat_desktop.png';
import RecommendationMiniCatMobileImage from '../../../assets/images/recommendation_mini_cat_mobile.png';
import { ReactComponent as Check } from '../../../assets/images/icons/check_yellow.svg';
import { Species, useProductQuiz } from '../ProductQuizContext';
import { useHistory } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import { isMobileDevice } from '../../../lib/util/isMobileDevice';
import { quizQuestionPage } from '../../../lib/analytics/events';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { getFiGrowthBook } from '../../../lib/growthbook';

export enum RecommendationType {
  Mini = 'mini',
  Collar = 'collar',
}

const COLLAR_BENEFITS = [
  'Longest Battery Life',
  'Highly durable',
  'Fully waterproof',
  'Enhanced GPS tracking',
  'Health Tracking',
  'LED light',
];

const MINI_BENEFITS = [
  'Lightweight & comfortable',
  'Attachable to any collar or harness',
  'Reliable escape alerts',
  'Waterproof',
];

const shopButton = (history: any, recommendation: RecommendationType, t: TFunction, species?: Species | null) => {
  const isMobile = isMobileDevice();

  return (
    <Flex justify="left" className="noHighlight" mt={10}>
      <Button
        w={isMobile ? '100%' : '50%'}
        radius={100}
        h={56}
        style={{ color: 'black', backgroundColor: 'white' }}
        onClick={() => {
          quizQuestionPage.quizSuggestedDeviceShop();
          if (recommendation === RecommendationType.Collar) {
            history.push(AppPaths.Products('smart-collar-v3b'));
          } else {
            history.push(`${AppPaths.Products('fi-mini')}?type=${species ?? Species.Dog}`);
          }
        }}
      >
        <Text size={16} py={10}>
          {recommendation === RecommendationType.Collar ? t('shop_fi_series_3_plus') : t('shop_fi_mini')}
        </Text>
      </Button>
    </Flex>
  );
};

const recommendationImage = (species: Species | null, recommendation: RecommendationType) => {
  const isMobile = isMobileDevice();

  const collarImage = (
    <Box sx={{ height: isMobile ? '15vh' : '26vh', width: '100%', overflow: 'hidden', borderRadius: 16 }}>
      <Image
        image={{ type: 'image', url: isMobile ? RecommendationCollarMobileImage : RecommendationCollarDesktopImage }}
        style={{
          height: '100%',
          width: '100%',
          objectFit: 'cover',
        }}
      />
    </Box>
  );

  const miniImage = (
    <Box sx={{ height: isMobile ? '23dvh' : '30vh', width: '100%', overflow: 'hidden', borderRadius: 16 }}>
      <Image
        image={{
          type: 'image',
          url:
            species === Species.Cat
              ? isMobile
                ? RecommendationMiniCatMobileImage
                : RecommendationMiniCatDesktopImage
              : isMobile
                ? RecommendationMiniDogMobileImage
                : RecommendationMiniDogDesktopImage,
        }}
        style={{
          height: '100%',
          width: '100%',
          objectFit: 'cover',
        }}
      />
    </Box>
  );

  return <>{recommendation === RecommendationType.Collar ? collarImage : miniImage}</>;
};

export const Recommendation: React.FC<{ recommendation: RecommendationType }> = ({ recommendation }) => {
  const { t } = useTranslation();
  const { quizAnswers } = useProductQuiz();
  const history = useHistory();
  const isMobile = isMobileDevice();
  const [isFreeTrialEnabled] = useState(() => {
    return getFiGrowthBook().getFeatureValue<boolean>('free-trial-enabled', false);
  });

  return (
    <>
      <Flex direction="column" gap={4}>
        <Text
          size={16}
          weight={600}
          lh={1.4}
          sx={(theme) => ({
            color: theme.colors['type-dark-secondary'],
          })}
        >
          {t('for_name_we_recommend', { name: quizAnswers.petName })}
        </Text>
        <Text
          size={40}
          weight={700}
          lh={1.05}
          ta="left"
          sx={{
            letterSpacing: '-1.12px',
          }}
        >
          {recommendation === RecommendationType.Collar ? t('series_3_plus') : t('fi_mini')}
        </Text>
      </Flex>
      {recommendationImage(quizAnswers.species, recommendation)}
      <Grid w="100%" gutter={8}>
        {(recommendation === RecommendationType.Collar ? COLLAR_BENEFITS : MINI_BENEFITS).map((benefit) => (
          <Grid.Col span={isMobile ? 12 : 6} key={benefit}>
            <Flex gap={8} align="center">
              <Check />
              <Text size={16} weight={600} lh={1.4}>
                {benefit}
              </Text>
            </Flex>
          </Grid.Col>
        ))}
      </Grid>
      {shopButton(history, recommendation, t, quizAnswers.species)}
      {isFreeTrialEnabled && (
        <Flex justify="left" className="noHighlight" mt={8}>
          <Button
            w={isMobile ? '100%' : '50%'}
            radius={100}
            h={56}
            variant="outline"
            style={{ color: 'white', borderColor: 'white' }}
            onClick={() => {
              quizQuestionPage.quizFreeTrialButtonTapped();
              if (recommendation === RecommendationType.Collar) {
                history.push(AppPaths.Products('smart-collar-v3b'));
              } else {
                history.push(`${AppPaths.Products('fi-mini')}?type=${quizAnswers.species ?? Species.Dog}`);
              }
            }}
          >
            <Text size={16} py={10}>
              {t('try_refurbished_for_free')}
            </Text>
          </Button>
        </Flex>
      )}
    </>
  );
};
